/**
 * EventHighlightUploader — Iter-313j follow-up.
 *
 * Dialog that lets a talent/client select one of their completed shifts,
 * upload a photo (via existing Cloudinary signature flow), add a caption,
 * and submit to `POST /api/event-highlights` (status='pending').
 *
 * Reused from:
 *   • EventHighlightsWidget empty-state CTA on TalentHome
 *   • CommunityFeed "Share a highlight" button
 *   • TalentBookingDetail post-shift Share flow
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Upload, Image as ImageIcon, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { downscaleImage } from '../utils/imageDownscale';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB

export const EventHighlightUploader = ({
  open,
  onOpenChange,
  onUploaded,
  defaultShiftId = null,
  triggerLabel = 'Share a highlight',
}) => {
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [shiftId, setShiftId] = useState(defaultShiftId || '');
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setShiftId(defaultShiftId || '');
    setCaption('');
    setFile(null);
    setPreview(null);
    (async () => {
      setLoadingBookings(true);
      try {
        const res = await api.get('/talent/bookings?status=completed&limit=20');
        const list = res.data?.bookings || res.data || [];
        setBookings(Array.isArray(list) ? list : []);
      } catch {
        setBookings([]);
      } finally {
        setLoadingBookings(false);
      }
    })();
  }, [open, defaultShiftId]);

  const shiftOptions = useMemo(() => {
    return (bookings || [])
      .filter((b) => b.shift_id)
      .map((b) => ({
        shift_id: b.shift_id,
        label: `${b.venue_name || b.event_title || 'Event'} · ${b.shift_date || ''}${b.city ? ' · ' + b.city : ''}`,
      }));
  }, [bookings]);

  const onPickFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please pick an image file.');
      return;
    }
    f = await downscaleImage(f); // mobile camera photos are 6-15MB raw
    if (f.size > MAX_IMAGE_BYTES) {
      toast.error('Image is too large. Max 10 MB.');
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const submit = async () => {
    if (!file) { toast.error('Please pick a photo to share.'); return; }
    if (!shiftId) { toast.error('Please pick which event this photo is from.'); return; }
    setUploading(true);
    try {
      // 1. Get Cloudinary signature
      const sigRes = await api.post('/cloudinary/signature', {
        folder: 'ieventur/event-highlights',
        resource_type: 'image',
      });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;

      // 2. Upload directly to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', 'ieventur/event-highlights');

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formData },
      );
      const uploadData = await uploadRes.json();
      if (!uploadData.secure_url) throw new Error('Upload failed');

      // 3. Persist the highlight
      await api.post('/event-highlights', {
        photo_url: uploadData.secure_url,
        caption: caption.trim() || null,
        shift_id: shiftId,
      });

      toast.success('Photo submitted — pending moderation');
      onOpenChange?.(false);
      onUploaded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to submit photo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="event-highlight-uploader">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-600" /> {triggerLabel}
          </DialogTitle>
          <DialogDescription>
            Share a moment from a shift you worked. Highlights are reviewed by our team before appearing on the public Community Feed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Photo</Label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center border-2 border-dashed border-cyan-200 rounded-lg p-4 cursor-pointer hover:border-cyan-400 bg-cyan-50/40 transition"
              data-testid="event-highlight-file-picker"
            >
              {preview ? (
                <img src={preview} alt="preview" className="max-h-56 rounded object-contain" />
              ) : (
                <>
                  <ImageIcon className="w-8 h-8 text-cyan-500 mb-1" />
                  <p className="text-xs text-gray-600 text-center">Tap to pick a photo (JPG/PNG, up to 10 MB)</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0])}
                data-testid="event-highlight-file-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Which event was this from?</Label>
            <Select value={shiftId} onValueChange={setShiftId} disabled={loadingBookings}>
              <SelectTrigger data-testid="event-highlight-shift-select">
                <SelectValue placeholder={loadingBookings ? 'Loading your bookings…' : 'Pick a completed shift'} />
              </SelectTrigger>
              <SelectContent>
                {shiftOptions.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-500">
                    No completed shifts yet. Highlights can only be shared from shifts you worked.
                  </div>
                )}
                {shiftOptions.map((o) => (
                  <SelectItem key={o.shift_id} value={o.shift_id} data-testid={`event-highlight-shift-${o.shift_id}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Caption (optional)</Label>
            <Textarea
              rows={2}
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 240))}
              placeholder="Say something about this moment…"
              data-testid="event-highlight-caption"
            />
            <div className="text-[10px] text-gray-400 text-right mt-0.5">{caption.length}/240</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange?.(false)} disabled={uploading}>Cancel</Button>
          <Button
            size="sm"
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            onClick={submit}
            disabled={uploading || !file || !shiftId}
            data-testid="event-highlight-submit"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Submit for review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EventHighlightUploader;
